import React, { ComponentPropsWithRef, HTMLInputTypeAttribute, ReactElement, ReactNode, memo } from 'react'
import styled from 'styled-components'
import cn from 'classnames'

import { LockIcon } from '../../lib/icons/lock-icon'
import { theme } from '../../lib/theme'
import { InfoIcon } from '../../lib/icons/info-icon'

import Tooltip from './Tooltip'

const SDiv = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    font-weight: var(--font-weight-light);
    margin-top: 4px;

    &.rounded {
        > div,
        * input {
            border-radius: 99em;
        }
    }
`

const SLabel = styled.label<{ required?: boolean }>`
    cursor: pointer;
    line-height: 1.5;
    position: absolute;
    font-size: 14px;
    margin: unset;
    transition: all 0.1s ease-in-out;
    padding: 0 1px;
    margin-left: 11px;
    top: 8px;
    font-weight: var(--font-weight-light);
    pointer-events: none;

    &.has-error {
        color: ${({ theme }) => theme.font.errorColor};
    }

    ${({ required }) =>
        required
            ? `&:after {
        content: ' *';
    }`
            : ''}
`

const SInputDiv = styled.div`
    border: 1px solid ${({ theme }) => theme.input.borderColor};
    background-color: ${({ theme }) => theme.font.backgroundColor};
    display: flex;
    align-items: center;
    position: relative;

    &.has-error {
        border-width: 1px;
        border-color: ${({ theme }) => theme.font.errorColor};

        input:focus {
            outline: none;
        }
    }

    &.no-border {
        background-color: unset;
        border: none;
    }
`

const SUnitSpan = styled.div`
    display: flex;
    padding: 0 ${({ theme }) => theme.sizing.margin.m};
`

const SSubtitleDiv = styled.div`
    color: ${({ theme }) => theme.brandColors.CA_Dark_Gray};
    margin-top: 6px;

    &[disabled] {
        color: ${({ theme }) => theme.brandColors.CA_Light_Gray};
    }
`

const SErrorDiv = styled.div`
    margin-top: 5px;
    color: ${({ theme }) => theme.font.errorColor};
`

interface IInputProps extends ComponentPropsWithRef<'div'> {
    children: ReactNode
    disabled?: boolean
    disabledReason?: string
    dropdownMenu?: ReactNode
    error?: string
    id?: string
    unit?: ReactNode
    subtitle?: ReactNode
    subtitlePosition?: 'left' | 'right'
    labelText?: string
    infoText?: string
    infoTextValues?: Record<string, unknown>
    type?: HTMLInputTypeAttribute
    required?: boolean
}

const InputWrapper = memo(function InputWrapper({
    children,
    labelText,
    infoText,
    infoTextValues,
    id,
    className,
    required,
    unit,
    disabled,
    disabledReason,
    dropdownMenu,
    error,
    subtitle,
    subtitlePosition,
    type,
    onClick,
}: IInputProps): ReactElement {
    const showAdditionalInfo = unit || (disabled && disabledReason) || infoText

    return (
        <SDiv className={className}>
            <SInputDiv
                className={cn({
                    'no-border': type === 'checkbox',
                    'has-error': error,
                })}
                onClick={onClick}
            >
                {children}
                {labelText && (
                    <SLabel
                        data-testid={`label-text-${id}`}
                        htmlFor={id}
                        className={cn({ 'has-error': error, 'required-label': required })}
                    >
                        {labelText}
                    </SLabel>
                )}
                {showAdditionalInfo && (
                    <SUnitSpan>
                        {unit}
                        {disabledReason && (
                            <Tooltip content={disabledReason}>
                                <LockIcon size={15} fill={theme.brandColors.CA_Mid_Gray} />
                            </Tooltip>
                        )}
                        {infoText && (
                            <Tooltip content={infoText} contentValues={infoTextValues}>
                                <InfoIcon size={20} fill={theme.brandColors.CA_Mid_Gray} />
                            </Tooltip>
                        )}
                    </SUnitSpan>
                )}
                {dropdownMenu}
            </SInputDiv>
            {error && <SErrorDiv data-testid={`error-text-${id}`}>{error}</SErrorDiv>}
            {subtitle && (
                <SSubtitleDiv
                    {...(disabled && { disabled: disabled })}
                    className={cn(subtitlePosition === 'right' && 'ml-auto')}
                >
                    {subtitle}
                </SSubtitleDiv>
            )}
        </SDiv>
    )
})

export default InputWrapper
